import React from 'react';

import { FormattedMessage } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { FlagsProvider } from '@atlaskit/flag';
import CrossIcon from '@atlaskit/icon/core/cross';
import Lozenge from '@atlaskit/lozenge';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, Stack, Text, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { messages } from './messages';

const titleWrapperStyles = xcss({
	minWidth: '270px',
	paddingBlock: 'space.100',
});

const iconContainerStyles = xcss({
	height: '40px',
	borderColor: 'color.border',
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderRadius: 'radius.small',
});

const closeIconWrapperStyles = xcss({
	marginLeft: 'auto',
});

const containerStyles = xcss({
	height: '100%',
});

type ExtensionsConfigWrapperProps = React.PropsWithChildren<{
	buttonIcon?: React.ReactNode;
	closeConfigPanel: () => void;
	footerComponent?: React.FC;
	icon?: React.ReactNode;
	lozenge?: React.ReactNode;
	shouldUseCardV2Styles?: boolean;
	title: React.ReactNode;
}>;

export const ExtensionsConfigWrapper = ({
	title,
	icon,
	children,
	footerComponent,
	buttonIcon = null,
	shouldUseCardV2Styles = false,
	closeConfigPanel,
	lozenge,
}: ExtensionsConfigWrapperProps): React.JSX.Element => {
	return (
		<FlagsProvider>
			<Flex
				columnGap="space.300"
				justifyContent="space-between"
				direction="column"
				xcss={containerStyles}
			>
				<Stack space="space.300">
					<Flex
						justifyContent="start"
						alignItems="center"
						gap="space.100"
						xcss={titleWrapperStyles}
					>
						{buttonIcon}
						{icon && !shouldUseCardV2Styles && (
							<Flex
								justifyContent="center"
								alignItems="center"
								testId="config-panel-icon"
								xcss={iconContainerStyles}
							>
								{icon}
							</Flex>
						)}
						<Text size="large" weight={shouldUseCardV2Styles ? 'medium' : 'bold'}>
							{title}
						</Text>
						{/**
						 *  @todo when cleaning up this prop (and platformization is done), removed this !shouldUseCardV2Styles
						 * lozenge logic and replace all these usages to use the lozenge prop */}
						{!shouldUseCardV2Styles && (
							<Flex alignItems="center">
								<Lozenge appearance="new">
									<FormattedMessage {...messages.newLozengeLabel} />
								</Lozenge>
							</Flex>
						)}
						{lozenge && <Flex alignItems="center">{lozenge}</Flex>}
						<Box xcss={closeIconWrapperStyles}>
							<Tooltip hideTooltipOnClick content={<FormattedMessage {...messages.close} />}>
								<IconButton
									onClick={closeConfigPanel}
									appearance="subtle"
									icon={CrossIcon}
									label={<FormattedMessage {...messages.close} />}
									testId="panel-close-button"
									isTooltipDisabled={false}
								/>
							</Tooltip>
						</Box>
					</Flex>
					{children}
				</Stack>
				{footerComponent && footerComponent({})}
			</Flex>
		</FlagsProvider>
	);
};
